import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from "@material-ui/core"
import { Autocomplete } from "@material-ui/lab"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useMemo, useState } from "react"
import { exportarCSV } from "../../../services/insumos"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"

const COLS = [
    { key: "nome", label: "Nome", width: 140 },
    { key: "qtdEntrada", label: "qtd.Entrada", width: 100, align: "right" },
    { key: "qtdSaida", label: "qtd.Saída", width: 100, align: "right" },
    { key: "qtdAtual", label: "qtd.Atual", width: 120, align: "right" },
    { key: "fornecedor", label: "Fornecedor", width: 200, ellipsis: true },
    { key: "pedido", label: "Pedido", width: 100, align: "right" },
    { key: "custo", label: "Custo", width: 100 },
    { key: "data", label: "Data", width: 120 },
]

const cellStyle = (w, align) => ({
    minWidth: w,
    maxWidth: w,
    width: w,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: align || "left",
})

function formatBRWhileTyping(value) {
    const d = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 8)
    const p = []
    if (d.length > 0) p.push(d.slice(0, 2))
    if (d.length >= 3) p.push(d.slice(2, 4))
    if (d.length >= 5) p.push(d.slice(4, 8))
    return p.join("/")
}

function brToISO(str) {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(str || "")
    if (!m) return null
    const [, dd, mm, yyyy] = m
    const d = Number(dd),
        mo = Number(mm),
        y = Number(yyyy)
    const dt = new Date(y, mo - 1, d)
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null
    return `${yyyy}-${mm}-${dd}`
}

const isISO = s => /^\d{4}-\d{2}-\d{2}$/.test(s || "")

function isoToBR(iso) {
    if (!isISO(iso)) return ""
    const [y, m, d] = iso.split("-")
    return `${d}/${m}/${y}`
}

function dateObjToBR(dt) {
    if (!(dt instanceof Date) || isNaN(dt)) return ""
    const d = String(dt.getDate()).padStart(2, "0")
    const m = String(dt.getMonth() + 1).padStart(2, "0")
    const y = dt.getFullYear()
    return `${d}/${m}/${y}`
}

const brl = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
})
const formatBRL = v => brl.format(Number(v || 0))

export default function DialogHistorico({
    open,
    setOpenHistorico,
    historico,
    historicoBuscar,
    dataHoje,
}) {
    const setLoading = useLoadingStore(s => s.setLoading)
    const { enqueueSnackbar } = useSnackbar()
    const alertStart = (msg, tipo) => {
        enqueueSnackbar(msg, { variant: tipo })
    }
    const configs = useAppStore(s => s.configuracoes)
    const idEstabelecimento = configs?.user?.parametros?.estabelecimento?.id
    const autorizacao = localStorage.getItem("chaveSistema")

    const initialBR = useMemo(() => {
        if (isISO(dataHoje)) return isoToBR(dataHoje)
        if (dataHoje instanceof Date) return dateObjToBR(dataHoje)
        const todayISO = new Date().toISOString().slice(0, 10)
        return formatBRWhileTyping(isISO(dataHoje) ? isoToBR(dataHoje) : dataHoje || todayISO)
    }, [dataHoje])

    const initialISO = useMemo(() => {
        return brToISO(initialBR) || new Date().toISOString().slice(0, 10)
    }, [initialBR])

    const [insumoFilter, setInsumoFilter] = useState("")
    const [inicioMask, setInicioMask] = useState(initialBR)
    const [fimMask, setFimMask] = useState(initialBR)
    const [inicio, setInicio] = useState(initialISO)
    const [fim, setFim] = useState(initialISO)

    useEffect(() => {
        setInicioMask(initialBR)
        setFimMask(initialBR)
        setInicio(initialISO)
        setFim(initialISO)
    }, [initialBR, initialISO])

    const insumoNomes = useMemo(() => {
        const set = new Set((historico ?? []).map(h => h?.insumo?.nome).filter(Boolean))
        return Array.from(set).sort((a, b) =>
            String(a).localeCompare(String(b), "pt-BR", { sensitivity: "base" })
        )
    }, [historico])

    const historicoFiltrado = useMemo(() => {
        if (!insumoFilter) return historico ?? []
        const alvo = insumoFilter.toLocaleLowerCase("pt-BR")
        return (historico ?? []).filter(
            h => (h?.insumo?.nome ?? "").toLocaleLowerCase("pt-BR") === alvo
        )
    }, [historico, insumoFilter])

    const buscar = () => {
        if (isISO(inicio) && isISO(fim)) {
            historicoBuscar(inicio, fim)
        }
    }

    const buscarDisabled = !isISO(inicio) || !isISO(fim)

    const handleExport = async () => {
        if (buscarDisabled) return
        try {
            setLoading(true)
            const { blob, filename } = await exportarCSV(
                idEstabelecimento,
                inicio,
                fim,
                autorizacao
            )

            const a = document.createElement("a")
            a.href = URL.createObjectURL(blob)
            a.download = filename || `historico-${inicio}-a-${fim}.csv`
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(a.href)
            alertStart("CSV exportado com sucesso.", "success")
        } catch (error) {
            console.error(error)
            alertStart("Erro ao exportar CSV.", "error")
        } finally {
            setLoading(false)
        }
    }
    const insumoOptions = useMemo(() => ["", ...insumoNomes], [insumoNomes])

    return (
        <Dialog
            open={open}
            onClose={() => setOpenHistorico(false)}
            fullWidth
            maxWidth="lg"
            scroll="paper"
        >
            <DialogTitle>
                <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
                    <Grid item xs={12} md={12} lg={5}>
                        Histórico
                    </Grid>

                    <Grid item xs={12} md={12} lg={2}>
                        <Autocomplete
                            options={insumoOptions}
                            value={insumoFilter}
                            onChange={(_, newValue) => setInsumoFilter(newValue ?? "")}
                            getOptionLabel={opt => (opt ? String(opt) : "Todos")}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Insumo"
                                    variant="outlined"
                                    size="small"
                                    placeholder="Digite para filtrar…"
                                />
                            )}
                            clearOnEscape
                            autoHighlight
                            disableListWrap
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={2}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="Data Início"
                            placeholder="dd/mm/aaaa"
                            fullWidth
                            value={inicioMask}
                            onChange={e => {
                                const mask = formatBRWhileTyping(e.target.value)
                                setInicioMask(mask)
                                setInicio(brToISO(mask))
                            }}
                            inputProps={{
                                inputMode: "numeric",
                                pattern: "\\d{2}/\\d{2}/\\d{4}",
                                maxLength: 10,
                            }}
                            onKeyDown={e => {
                                if (e.key === "Enter" && !buscarDisabled) buscar()
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={2}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="Data Fim"
                            placeholder="dd/mm/aaaa"
                            fullWidth
                            value={fimMask}
                            onChange={e => {
                                const mask = formatBRWhileTyping(e.target.value)
                                setFimMask(mask)
                                setFim(brToISO(mask))
                            }}
                            inputProps={{
                                inputMode: "numeric",
                                pattern: "\\d{2}/\\d{2}/\\d{4}",
                                maxLength: 10,
                            }}
                            onKeyDown={e => {
                                if (e.key === "Enter" && !buscarDisabled) buscar()
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={1}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={buscar}
                            disabled={buscarDisabled}
                            style={{
                                backgroundColor: "green",
                                color: "white",
                            }}
                        >
                            Buscar
                        </Button>
                    </Grid>
                </Grid>
            </DialogTitle>

            <DialogContent style={{ padding: 0 }}>
                <TableContainer style={{ maxHeight: 560 }}>
                    <Table stickyHeader size="small" aria-label="Histórico de movimentações">
                        <TableHead>
                            <TableRow>
                                {COLS.map(c => (
                                    <TableCell key={c.key} style={cellStyle(c.width, c.align)}>
                                        {c.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {historicoFiltrado?.map((h, i) => {
                                const unidade = h?.insumo?.unidadeDeMedida ?? ""
                                const valorUnitario = Number(h?.insumo?.valor_unitario ?? 0)

                                const row = {
                                    nome: h?.insumo?.nome ?? "-",
                                    qtdEntrada: h?.quantidade_entrada
                                        ? `${h?.quantidade_entrada} ${unidade}`
                                        : "-",
                                    qtdSaida: h?.quantidade_saida
                                        ? `${h?.quantidade_saida} ${unidade}`
                                        : "-",
                                    qtdAtual: h?.quantidade ? `${h?.quantidade} ${unidade}` : "-",
                                    fornecedor: h?.fornecedor ?? "-",
                                    pedido: h?.pedido_id ?? "-",
                                    custo: h?.quantidade_entrada
                                        ? formatBRL(h?.quantidade_entrada * valorUnitario)
                                        : h?.quantidade_saida
                                        ? formatBRL(h?.quantidade_saida * valorUnitario)
                                        : formatBRL(valorUnitario),
                                    data: h?.data
                                        ? isISO(h.data)
                                            ? isoToBR(h.data)
                                            : h.data
                                        : "-",
                                }

                                return (
                                    <TableRow hover key={`${row.pedido}-${i}`}>
                                        {COLS.map(c => {
                                            const value = row[c.key]
                                            const content = c.ellipsis ? (
                                                <Tooltip title={String(value)} placement="top">
                                                    <span>{value}</span>
                                                </Tooltip>
                                            ) : (
                                                value
                                            )

                                            return (
                                                <TableCell
                                                    key={c.key}
                                                    style={cellStyle(c.width, c.align)}
                                                    align={c.align}
                                                >
                                                    {content}
                                                </TableCell>
                                            )
                                        })}
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="contained"
                    style={{ backgroundColor: "gray", color: "white" }}
                    onClick={handleExport}
                    disabled={buscarDisabled}
                >
                    Exportar
                </Button>
                <Button variant="contained" color="primary" onClick={() => setOpenHistorico(false)}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
